// Compiled default. The live value is the create_chat_free_turn_limit row in
// credit_settings (admin credits dashboard); this constant is the fallback
// when that row is missing or unreadable.
export const CREATE_CHAT_FREE_TURN_LIMIT = 5;
export const CREATE_CHAT_CREDIT_REQUIRED_CODE = "CREATE_CHAT_CREDIT_REQUIRED";

export type CreateChatTurn = {
  role: "user" | "assistant";
  content?: unknown;
  hidden?: boolean;
  imageUrl?: string;
  imageUrls?: string[];
};

function isSyntheticUploadTurn(turn: CreateChatTurn): boolean {
  if (!turn.imageUrl && (!turn.imageUrls || turn.imageUrls.length === 0)) {
    return false;
  }

  return (
    typeof turn.content === "string" &&
    /^uploaded (?:an|the) image\.?$/i.test(turn.content.trim())
  );
}

/**
 * Only visible, customer-authored design messages consume the free chat
 * allowance. Preset clicks and automatic image-review turns are persisted as
 * hidden user messages for model context, while the upload affordance emits a
 * visible but synthetic "Uploaded an image." message. None of those should
 * spend a customer's conversational allowance.
 */
export function isCountableCreateChatUserTurn(turn: CreateChatTurn): boolean {
  return (
    turn.role === "user" && turn.hidden !== true && !isSyntheticUploadTurn(turn)
  );
}

export function countCreateChatUserTurns(turns: CreateChatTurn[]): number {
  return turns.filter(isCountableCreateChatUserTurn).length;
}

export function shouldShowCreateChatCreditGate(params: {
  turns: CreateChatTurn[];
  hasCredits: boolean | undefined;
  isLoggedIn?: boolean;
  freeTurnLimit?: number;
  /** False for hidden actions and synthetic uploads. */
  currentTurnIsCustomerAuthored?: boolean;
}): boolean {
  if (params.currentTurnIsCustomerAuthored === false) {
    return false;
  }

  const freeTurnLimit = params.freeTurnLimit ?? CREATE_CHAT_FREE_TURN_LIMIT;
  if (countCreateChatUserTurns(params.turns) < freeTurnLimit) {
    return false;
  }

  // Only block client-side when we are CONFIDENT the user has no credits.
  // For a signed-in user, `hasCredits` is undefined while the balance is still
  // loading (it is deliberately null, not 0, until fetched). Gating on that
  // would flash the buy-credits modal at a paying user — common right after a
  // refresh/restore, when the conversation already has prior turns. In that
  // case defer to the server, which reads the real balance and bypasses
  // credited users. A signed-out visitor never has credits, so gate them.
  if (params.isLoggedIn && params.hasCredits === undefined) {
    return false;
  }

  return params.hasCredits !== true;
}
